import { validationProblemsTag } from '../../../apis/apiValidationProblems';
import {
  ApiOperationType,
  SkuClassConfigurationRequest,
  SkuClassConfigurationResponse,
  ValidationArea,
} from '@ydistri/api-sdk';
import { apiSlice, TemplateOrCalculation } from '../../../apis/api';
import { CalculationsCollection, TemplatesCollection } from '../../../swagger/collections';
import { SignalConfigurationChanged } from '../../../signalr/signalrInterfaces';
import { signalrClient } from '../../../signalr/client';
import { getTags } from '../../../apis/apiLib';

const { TAGS, TAGS_ARRAY } = getTags('skuClassConfiguration');

export interface UpdateSkuClassConfigurationPayload {
  templateOrCalculation: TemplateOrCalculation;
  skuClassId: number;
  request: SkuClassConfigurationRequest;
}

export const apiSkuClasses = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getSkuClassConfiguration: builder.query<
        SkuClassConfigurationResponse[],
        TemplateOrCalculation
      >({
        queryFn: async arg => {
          const {
            data: { data: skuClassConfiguration },
          } = await (arg.type === 'Template'
            ? TemplatesCollection.getTemplateSkuClassConfigurations(arg.id)
            : CalculationsCollection.getCalculationSkuClassConfigurations(arg.id));
          return { data: skuClassConfiguration };
        },
        providesTags: [TAGS.skuClassConfiguration],
        async onCacheEntryAdded({ type: _, id: selectedTemplateId }, api) {
          try {
            await api.cacheDataLoaded;

            const listener = (signal: SignalConfigurationChanged) => {
              // eslint-disable-next-line no-console -- we want to log this
              console.log('CONFIGURATION CHANGED', signal);
              if (selectedTemplateId !== signal.templateId) return;
              if (
                signal.entityType === 'SkuClass' &&
                signal.operationType === ApiOperationType.Patch
              ) {
                const skuClassId = signal.entityId;
                api.updateCachedData(draft => {
                  signal.updates.forEach(update => {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type of this
                    const field = update.fieldName as keyof SkuClassConfigurationResponse;
                    const value = update.value;
                    const skuClass = draft.find(r => r.skuClass?.id === skuClassId);
                    if (
                      skuClass &&
                      field in skuClass &&
                      field !== 'skuClass' &&
                      value !== undefined
                    ) {
                      skuClass[field] = value === '1';
                    }
                  });
                });
              }
            };

            signalrClient.on('TemplateConfigurationChanged', listener);

            api.cacheEntryRemoved.then(() => {
              signalrClient.off('TemplateConfigurationChanged', listener);
            });
          } catch {
            // no-op in case cache entry was removed before data was loaded
          }
          await api.cacheEntryRemoved;
        },
      }),

      updateSkuClassConfiguration: builder.mutation<
        SkuClassConfigurationResponse,
        UpdateSkuClassConfigurationPayload
      >({
        invalidatesTags: (result, error, { templateOrCalculation }) => [
          validationProblemsTag(templateOrCalculation, ValidationArea.SkuClasses),
        ],
        queryFn: async payload => {
          const { data } = await TemplatesCollection.patchTemplateSkuClassConfigurations(
            payload.templateOrCalculation.id,
            payload.skuClassId,
            payload.request,
          );

          return data;
        },
        onQueryStarted(payload, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            apiSkuClasses.util.updateQueryData(
              'getSkuClassConfiguration',
              payload.templateOrCalculation,
              draft => {
                const skuClass = draft.find(r => r.skuClass?.id === payload.skuClassId);
                if (skuClass) Object.assign(skuClass, payload.request);
              },
            ),
          );
          queryFulfilled.catch(patchResult.undo);
        },
      }),
    }),
  });

export const { useGetSkuClassConfigurationQuery, useUpdateSkuClassConfigurationMutation } =
  apiSkuClasses;
